<template>
    <div class="container mx-auto p-6">
        <!-- Header Section -->
        <div class="flex flex-col md:flex-row md:items-center justify-between mb-8">
            <div class="mb-4 md:mb-0">
                <h2 class="text-2xl lg:text-3xl font-bold text-gray-900">Search Jobs</h2>
                <p class="text-gray-600 mt-1 text-sm lg:text-base">Discover job opportunities matched to your skills and experience</p>
            </div>
            <div class="bg-white rounded-full shadow-md border border-gray-200 p-1.5">
                <div class="flex space-x-1">
                    <button @click="viewMode = 'list'" class="px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200" 
                            :class="viewMode === 'list' ? 'bg-sky-500 text-white' : 'text-gray-700 hover:bg-gray-100'">
                        <span class="flex items-center">
                            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                            </svg>
                            List View
                        </span>
                    </button>
                    <button @click="viewMode = 'map'" class="px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200"
                            :class="viewMode === 'map' ? 'bg-sky-500 text-white' : 'text-gray-700 hover:bg-gray-100'">
                        <span class="flex items-center">
                            <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
                            </svg>
                            Map View
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <div class="mb-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div class="relative flex-1">
                    <label for="job-search" class="sr-only">Search roles or companies</label>
                    <span class="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 pointer-events-none">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                        </svg>
                    </span>
                    <input
                        id="job-search"
                        v-model.trim="searchQuery"
                        type="search"
                        placeholder="Search by role title, company, location or industry"
                        class="w-full rounded-2xl border border-gray-200 bg-white py-4 pl-12 pr-4 text-base text-gray-900 transition-colors duration-200 placeholder:text-gray-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    >
                </div>

                <div class="inline-flex items-center rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 border border-sky-100 whitespace-nowrap">
                    <template v-if="displayedJobsCount > 0">
                        Displaying {{ paginationStart.toLocaleString() }}-{{ paginationEnd.toLocaleString() }}
                        of {{ displayedJobsCount.toLocaleString() }}
                    </template>
                    <template v-else>
                        Displaying 0
                    </template>
                    {{ displayedJobsCount === 1 ? 'Job' : 'Jobs' }}
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="bg-white shadow-lg rounded-2xl p-6">
            <div class="flex flex-col space-y-5 w-full">
                <div class="flex justify-between items-center">
                    <div class="h-8 w-40 bg-gray-200 rounded-full animate-pulse"></div>
                    <div class="h-8 w-32 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
                <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="i in 6" :key="i" class="bg-gray-100 rounded-2xl p-4 h-64 animate-pulse shadow transition-shadow duration-300"></div>
                </div>
            </div>
        </div>

        <div v-else class="grid gap-6 xl:grid-cols-[280px,minmax(0,1fr)]">
            <aside class="h-fit rounded-2xl border border-gray-200 bg-white p-5 shadow-sm xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)] xl:overflow-y-auto xl:overscroll-contain">
                <div class="mb-5 flex items-center justify-between">
                    <div>
                        <h3 class="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">Filters</h3>
                        <p class="mt-1 text-sm text-gray-600">Refine the current jobs list.</p>
                    </div>
                    <button
                        type="button"
                        class="text-sm font-semibold text-sky-600 hover:text-sky-700"
                        @click="clearFilters"
                    >
                        Reset
                    </button>
                </div>

                <div class="space-y-5">
                    <div>
                        <label for="job-sort" class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Sort</label>
                        <select
                            id="job-sort"
                            v-model="sortBy"
                            class="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                        >
                            <option value="date_desc">Date Posted: Newest</option>
                            <option value="date_asc">Date Posted: Oldest</option>
                            <option value="title_asc">A to Z</option>
                            <option value="title_desc">Z to A</option>
                        </select>
                    </div>

                    <div>
                        <label for="job-category-filter" class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Category</label>
                        <select
                            id="job-category-filter"
                            v-model="selectedCategory"
                            class="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                        >
                            <option value="">All categories</option>
                            <option
                                v-for="category in categoryOptions"
                                :key="category.value"
                                :value="category.value"
                            >
                                {{ category.label }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label for="job-subcategory-filter" class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Subcategory</label>
                        <select
                            id="job-subcategory-filter"
                            v-model="selectedSubcategory"
                            :disabled="!selectedCategory"
                            class="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                        >
                            <option value="">{{ selectedCategory ? 'All subcategories' : 'Select a category first' }}</option>
                            <option
                                v-for="subcategory in subcategoryOptions"
                                :key="subcategory.value"
                                :value="subcategory.value"
                            >
                                {{ subcategory.label }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label for="job-company-filter" class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Company</label>
                        <select
                            id="job-company-filter"
                            v-model="selectedCompany"
                            class="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                        >
                            <option value="">All companies</option>
                            <option v-for="company in companyOptions" :key="company" :value="company">{{ company }}</option>
                        </select>
                    </div>

                    <div>
                        <label for="job-date-filter" class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date Posted</label>
                        <select
                            id="job-date-filter"
                            v-model="selectedPostedDate"
                            class="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                        >
                            <option value="">Any time</option>
                            <option value="7">Last 7 days</option>
                            <option value="14">Last 14 days</option>
                            <option value="30">Last 30 days</option>
                        </select>
                    </div>

                    <div>
                        <p class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">State</p>
                        <div class="space-y-2 rounded-2xl border border-gray-100 bg-gray-50 p-3">
                            <label
                                v-for="state in stateOptions"
                                :key="state.value"
                                class="flex items-center gap-2 text-sm text-gray-700"
                            >
                                <input
                                    :checked="selectedStates.includes(state.value)"
                                    type="checkbox"
                                    class="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                    @change="toggleState(state.value)"
                                />
                                <span>{{ state.label }}</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <p class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Industry</p>
                        <div class="space-y-2 rounded-2xl border border-gray-100 bg-gray-50 p-3">
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedIndustry" type="radio" value="" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>All industries</span>
                            </label>
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedIndustry" type="radio" value="mining" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>Mining</span>
                            </label>
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedIndustry" type="radio" value="energy" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>Energy</span>
                            </label>
                        </div>
                    </div>

                    <div>
                        <p class="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Work Type</p>
                        <div class="space-y-2 rounded-2xl border border-gray-100 bg-gray-50 p-3">
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedWorkType" type="radio" value="" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>All work types</span>
                            </label>
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedWorkType" type="radio" value="full-time" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>Full-time</span>
                            </label>
                            <label class="flex items-center gap-2 text-sm text-gray-700">
                                <input v-model="selectedWorkType" type="radio" value="contract" class="h-4 w-4 border-gray-300 text-sky-600 focus:ring-sky-500" />
                                <span>Contract</span>
                            </label>
                        </div>
                    </div>
                </div>
            </aside>

            <div class="min-w-0">
                <!-- List View - Modern Card Design -->
                <div v-if="viewMode === 'list'" class="space-y-4">
                    <div 
                        v-for="job in paginatedJobs" 
                        :key="job.job_id"
                        class="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-300 group overflow-hidden"
                    >
                        <div class="p-6">
                            <div class="flex items-start justify-between mb-4">
                                <div class="flex-1 min-w-0">
                                    <button
                                        type="button"
                                        class="mb-2 text-left"
                                        @click="openJob(job)"
                                    >
                                        <h3 class="text-xl font-bold text-gray-900 transition-colors duration-200 leading-tight hover:text-sky-600">
                                            {{ job.job_title }}
                                        </h3>
                                    </button>
                                    <div class="flex items-center space-x-3 mb-3">
                                        <img
                                            v-if="job.company_logo"
                                            :src="job.company_logo"
                                            :alt="`${job.company_name} logo`"
                                            class="h-8 w-8 shrink-0 rounded-md border border-gray-200 bg-white object-contain p-0.5"
                                        />
                                        <router-link
                                            v-if="job.company_slug"
                                            :to="companyRoute(job)"
                                            class="text-lg font-semibold text-gray-800 transition-colors duration-200 hover:text-sky-600 hover:underline"
                                        >
                                            {{ job.company_name }}
                                        </router-link>
                                        <span v-else class="text-lg font-semibold text-gray-800">{{ job.company_name }}</span>
                                        <span class="text-sm text-gray-500">•</span>
                                        <div class="flex items-center text-gray-600">
                                            <svg class="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                            </svg>
                                            <span class="text-sm font-medium">{{ job.job_location }}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div class="flex flex-col items-end space-y-2 ml-4">
                                    <div v-if="job.is_external" class="flex items-center px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-semibold border border-amber-200">
                                        External Opportunity
                                    </div>
                                    <div v-if="job.application_date" class="flex items-center px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold border border-emerald-200">
                                        <svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        Applied
                                    </div>
                                    <span class="text-xs text-gray-500 font-medium">
                                        {{ formattedPostedDate(job.job_date) }}
                                    </span>
                                </div>
                            </div>

                            <p v-if="job.job_summary" class="mb-4 whitespace-pre-line text-sm leading-6 text-gray-600">
                                {{ job.job_summary }}
                            </p>

                            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div class="flex items-center space-x-2">
                                    <div class="p-2 bg-blue-50 rounded-lg">
                                        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0H8m8 0v6a2 2 0 01-2 2H10a2 2 0 01-2-2V6m8 0h2a2 2 0 012 2v8a2 2 0 01-2 2h-2"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-xs text-gray-500 font-medium uppercase tracking-wide">Work Type</p>
                                        <p class="text-sm font-semibold text-gray-900 capitalize">{{ job.job_type?.replace('_', ' ') || 'Not specified' }}</p>
                                    </div>
                                </div>

                                <div class="flex items-center space-x-2">
                                    <div class="p-2 bg-green-50 rounded-lg">
                                        <svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-xs text-gray-500 font-medium uppercase tracking-wide">Rate</p>
                                        <p v-if="job.rate_category && job.rate_category !== 'custom_rate'" class="text-sm font-semibold text-gray-900">
                                            {{ job.rate_category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\s*Rate\s*/gi, '').trim() }}
                                        </p>
                                        <p v-else class="text-sm font-semibold text-gray-900">${{ job.job_rate }} / {{ job.job_rate_type }}</p>
                                    </div>
                                </div>

                                <div class="flex items-center space-x-2">
                                    <div class="p-2 bg-purple-50 rounded-lg">
                                        <svg class="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-xs text-gray-500 font-medium uppercase tracking-wide">{{ job.is_external ? 'Application Deadline' : 'Start Date' }}</p>
                                        <p class="text-sm font-semibold text-gray-900">
                                            {{ job.is_external ? formatClosingDate(job.closing_date) : (job.job_start || 'Flexible') }}
                                        </p>
                                    </div>
                                </div>

                                <div class="flex items-center space-x-2">
                                    <div class="p-2 bg-orange-50 rounded-lg">
                                        <svg class="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2-2h10m-9 0h9"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <p class="text-xs text-gray-500 font-medium uppercase tracking-wide">Industry</p>
                                        <p class="text-sm font-semibold text-gray-900">{{ job.job_industry }}</p>
                                    </div>
                                </div>
                            </div>

                            <div class="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
                                <div class="flex items-center space-x-2">
                                    <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                                        {{ job.job_role }}
                                    </span>
                                    <span v-if="job.job_type" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-700 border border-gray-200 capitalize">
                                        {{ job.job_type.replace('_', ' ') }}
                                    </span>
                                </div>
                                
                                <button 
                                    @click.stop="openJob(job)"
                                    class="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md"
                                    :class="isDirectExternal(job) ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500' : 'bg-sky-600 hover:bg-sky-700 focus:ring-sky-500'"
                                >
                                    {{ isDirectExternal(job) ? 'Apply Externally' : 'View Job' }}
                                    <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="displayedJobsCount === 0" class="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl shadow-sm border border-gray-100">
                        <div class="p-4 rounded-full bg-sky-50 mb-6">
                            <svg class="w-16 h-16 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                        </div>
                        <h3 class="text-2xl font-bold text-gray-900 mb-2">No matching jobs found</h3>
                        <p class="text-gray-600 max-w-md mx-auto text-lg">Try a different filter combination to broaden your search.</p>
                    </div>

                    <div v-else-if="totalPages > 1" class="rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
                        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p class="text-sm text-gray-600">
                                Showing <span class="font-semibold text-gray-900">{{ paginationStart.toLocaleString() }}-{{ paginationEnd.toLocaleString() }}</span>
                                of <span class="font-semibold text-gray-900">{{ displayedJobsCount.toLocaleString() }}</span> jobs
                            </p>

                            <div class="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
                                <button
                                    type="button"
                                    class="inline-flex h-10 items-center rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="currentPage === 1"
                                    @click="goToPreviousPage"
                                >
                                    Previous
                                </button>

                                <button
                                    v-for="page in paginationItems"
                                    :key="page.key"
                                    type="button"
                                    class="hidden h-10 min-w-10 items-center justify-center rounded-xl border px-3 sm:inline-flex text-sm font-semibold transition-colors duration-200"
                                    :class="page.type === 'ellipsis'
                                        ? 'cursor-default border-transparent text-gray-400'
                                        : page.number === currentPage
                                            ? 'border-sky-500 bg-sky-500 text-white'
                                            : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'"
                                    :disabled="page.type === 'ellipsis'"
                                    @click="page.type === 'page' && goToPage(page.number)"
                                >
                                    {{ page.label }}
                                </button>

                                <span class="text-sm font-semibold text-gray-700 sm:hidden">
                                    Page {{ currentPage }} of {{ totalPages }}
                                </span>

                                <button
                                    type="button"
                                    class="inline-flex h-10 items-center rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="currentPage === totalPages"
                                    @click="goToNextPage"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="md:col-span-2 bg-white shadow-lg rounded-2xl overflow-hidden">
                <div class="p-4 border-b border-gray-200 flex justify-between items-center bg-white">
                    <h3 class="text-lg font-medium text-gray-900">Locations</h3>
                    <div class="text-sm font-medium px-3 py-1 bg-sky-50 text-sky-600 rounded-full">{{ filteredJobsCount }} locations</div>
                </div>
                
                <div class="relative h-[calc(100vh-220px)] min-h-[500px]">
                    <!-- Map Container -->
                    <div ref="mapContainer" class="w-full h-full"></div>
                    
                    <!-- Loading Overlay -->
                    <div v-if="isMapLoading" class="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center">
                        <div class="flex flex-col items-center">
                            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
                            <p class="mt-3 text-sky-600 text-sm font-medium">Loading job locations...</p>
                        </div>
                    </div>
                    
                    <!-- Job Info Popup - Dynamic positioning -->
                    <div v-if="selectedJob" 
                         class="absolute bg-white p-5 rounded-xl shadow-xl border border-gray-200 transition-all duration-300 z-10"
                         :style="popupStyle">
                        <div class="flex justify-between items-start mb-3">
                            <h3 class="font-semibold text-gray-900 text-lg pr-2">{{ selectedJob.job_title }}</h3>
                            <button @click="clearSelection" class="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors duration-200 flex-shrink-0">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        <p class="text-sm font-medium text-gray-700">{{ selectedJob.company_name }}</p>
                        <p class="text-sm text-gray-600 mt-1 mb-2 flex items-center">
                            <svg class="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            {{ selectedJob.job_location }}
                        </p>
                        <div class="flex items-center mt-2 mb-3">
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="selectedJob.is_external ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'">
                                {{ formattedPostedDate(selectedJob.job_date) }}
                            </span>
                        </div>
                        <p v-if="selectedJob.job_summary" class="mb-3 whitespace-pre-line text-sm leading-5 text-gray-600">
                            {{ selectedJob.job_summary }}
                        </p>
                        <a
                            v-if="isDirectExternal(selectedJob)"
                            :href="selectedJob.external_job_url || undefined"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-full text-white bg-amber-500 hover:bg-amber-600 transition-colors duration-200 shadow-sm"
                        >
                            Apply Externally
                        </a>
                        <router-link v-else :to="'/contractor/job/' + selectedJob.job_slug" class="w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-full text-white bg-sky-500 hover:bg-sky-600 transition-colors duration-200 shadow-sm">
                            View Details
                        </router-link>
                    </div>
                </div>
                    </div>
                    
                    <div class="bg-white shadow-lg rounded-2xl overflow-hidden">
                <div class="sticky top-0 bg-white p-4 border-b border-gray-200 z-10">
                    <h3 class="text-lg font-medium text-gray-900 mb-1">Available Jobs</h3>
                    <p class="text-sm text-gray-500">{{ displayedJobsCount }} positions found</p>
                </div>
                
                <div class="overflow-auto h-[calc(100vh-230px)] min-h-[500px]">
                    <div v-if="noLocationJobs.length > 0" class="p-3 bg-amber-50 border-b border-amber-100">
                        <p class="text-xs text-amber-800 flex items-center">
                            <svg class="w-4 h-4 mr-1.5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            {{ noLocationJobs.length }} jobs without location information
                        </p>
                    </div>
                    
                    <div class="divide-y divide-gray-100">
                        <div 
                            v-for="job in paginatedJobs" 
                            :key="job.job_id" 
                            @click="handleSidebarJobClick(job)"
                            class="p-4 hover:bg-sky-50 cursor-pointer transition-all duration-200"
                            :class="{
                                'bg-sky-50 border-l-4 border-sky-500': selectedJob && selectedJob.job_id === job.job_id,
                                'opacity-60': !hasLocation(job) && viewMode === 'map'
                            }"
                        >
                            <div class="flex justify-between">
                                <h4 class="font-medium text-gray-900">{{ job.job_title }}</h4>
                                <span v-if="!hasLocation(job)" class="ml-2 flex-shrink-0">
                                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                    </svg>
                                </span>
                            </div>
                            <p class="text-sm text-gray-700 mt-1">{{ job.company_name }}</p>
                            <p v-if="job.job_summary" class="mt-1 whitespace-pre-line text-xs leading-5 text-gray-500">
                                {{ job.job_summary }}
                            </p>
                            <p class="text-xs text-gray-500 mt-1 flex items-center">
                                <svg class="w-3.5 h-3.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                                </svg>
                                {{ job.job_location || 'Location not specified' }}
                            </p>
                            <div class="flex justify-between items-center mt-3">
                                <span class="text-xs font-medium px-2 py-0.5 rounded-full" :class="job.is_external ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'">
                                    {{ formattedPostedDate(job.job_date) }}
                                </span>
                                <a
                                    v-if="isDirectExternal(job)"
                                    :href="job.external_job_url || undefined"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="text-amber-600 text-xs font-medium hover:text-amber-800 hover:underline"
                                    @click.stop
                                >
                                    Apply Externally
                                </a>
                                <router-link v-else :to="'/contractor/job/' + job.job_slug" class="text-sky-600 text-xs font-medium hover:text-sky-800 hover:underline">
                                    View Details
                                </router-link>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                <div v-if="displayedJobsCount > 0 && totalPages > 1" class="mt-6 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p class="hidden text-sm text-gray-600 sm:block">
                            Page <span class="font-semibold text-gray-900">{{ currentPage }}</span>
                            of <span class="font-semibold text-gray-900">{{ totalPages }}</span>
                        </p>

                        <div class="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
                            <button
                                type="button"
                                class="inline-flex h-10 items-center rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="currentPage === 1"
                                @click="goToPreviousPage"
                            >
                                Previous
                            </button>

                            <button
                                v-for="page in paginationItems"
                                :key="`bottom-${page.key}`"
                                type="button"
                                class="hidden h-10 min-w-10 items-center justify-center rounded-xl border px-3 sm:inline-flex text-sm font-semibold transition-colors duration-200"
                                :class="page.type === 'ellipsis'
                                    ? 'cursor-default border-transparent text-gray-400'
                                    : page.number === currentPage
                                        ? 'border-sky-500 bg-sky-500 text-white'
                                        : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'"
                                :disabled="page.type === 'ellipsis'"
                                @click="page.type === 'page' && goToPage(page.number)"
                            >
                                {{ page.label }}
                            </button>

                            <span class="text-sm font-semibold text-gray-700 sm:hidden">
                                Page {{ currentPage }} of {{ totalPages }}
                            </span>

                            <button
                                type="button"
                                class="inline-flex h-10 items-center rounded-xl border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition-colors duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="currentPage === totalPages"
                                @click="goToNextPage"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import config from '@/config';

import contractorService from '@/services/marketplace/contractor.service';
import JobCard from '@/components/marketplace/generic/JobCard'
import JobCardLoading from '@/components/marketplace/generic/JobCardLoading'

const JOB_STATE_FILTERS = [
    { value: 'NSW', label: 'NSW' },
    { value: 'QLD', label: 'QLD' },
    { value: 'WA', label: 'WA' },
    { value: 'SA', label: 'SA' },
    { value: 'VIC', label: 'VIC' },
    { value: 'TAS', label: 'TAS' },
    { value: 'NT', label: 'NT' },
    { value: 'ACT', label: 'ACT' },
    { value: 'Overseas', label: 'Overseas' }
];

const JOBS_PER_PAGE = 100;
const OTHER_FILTER_VALUE = '__other__';

export default {
    name: 'ContractorJobs',
    inject: ['toggleLoading'],
    components: {
        JobCard,
        JobCardLoading
    },
    data() {
        return {
            loading: true,
            jobs: [],
            searchQuery: '',
            sortBy: 'date_desc',
            selectedCompany: '',
            selectedCategory: '',
            selectedSubcategory: '',
            selectedPostedDate: '',
            selectedStates: [],
            selectedIndustry: '',
            selectedWorkType: '',
            currentPage: 1,
            applications: [],
            apps: [],
            viewMode: 'list', // 'list' or 'map'
            map: null,
            markers: [],
            selectedJob: null,
            selectedMarker: null,
            isMapLoading: false,
            mapScriptLoaded: false,
            bounds: null,
            markerCluster: null,
            popupPosition: { top: '20px', left: '20px' }
        };
    },
    computed: {
        companyOptions() {
            return this.getSortedUniqueValues(this.jobs.map(job => job.company_name));
        },
        categoryOptions() {
            return this.buildFilterOptions(this.jobs.map(job => this.getJobCategory(job)));
        },
        subcategoryOptions() {
            if (!this.selectedCategory) {
                return [];
            }

            const categoryJobs = this.jobs.filter(job =>
                this.matchesOptionFilter(this.selectedCategory, this.getJobCategory(job), this.categoryOptions)
            );

            return this.buildFilterOptions(categoryJobs.map(job => this.getJobSubcategory(job)));
        },
        stateOptions() {
            return JOB_STATE_FILTERS;
        },
        filteredJobs() {
            const query = this.searchQuery.trim().toLowerCase();
            let jobs = this.jobs.filter(job => {
                const title = (job.job_title || '').toLowerCase();
                const companyName = (job.company_name || '').toLowerCase();
                const location = (job.job_location || '').toLowerCase();
                const industry = (job.job_industry || '').toLowerCase();
                const category = this.getJobCategory(job).toLowerCase();
                const subcategory = this.getJobSubcategory(job).toLowerCase();

                const matchesQuery = !query || title.includes(query) ||
                    companyName.includes(query) ||
                    location.includes(query) ||
                    industry.includes(query) ||
                    category.includes(query) ||
                    subcategory.includes(query);

                const matchesCompany = !this.selectedCompany || (job.company_name || '') === this.selectedCompany;
                const matchesCategory = this.matchesOptionFilter(this.selectedCategory, this.getJobCategory(job), this.categoryOptions);
                const matchesSubcategory = !this.selectedCategory || this.matchesOptionFilter(this.selectedSubcategory, this.getJobSubcategory(job), this.subcategoryOptions);
                const matchesState = this.matchesStateFilter(job);
                const matchesIndustry = !this.selectedIndustry || this.normalizeIndustry(job) === this.selectedIndustry;
                const matchesWorkType = !this.selectedWorkType || this.normalizeWorkType(job) === this.selectedWorkType;
                const matchesPostedDate = this.matchesPostedDate(job);

                return matchesQuery &&
                    matchesCompany &&
                    matchesCategory &&
                    matchesSubcategory &&
                    matchesState &&
                    matchesIndustry &&
                    matchesWorkType &&
                    matchesPostedDate;
            });

            return jobs.sort((a, b) => this.compareJobs(a, b));
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filteredJobs.length / JOBS_PER_PAGE));
        },
        paginatedJobs() {
            const startIndex = (this.currentPage - 1) * JOBS_PER_PAGE;
            return this.filteredJobs.slice(startIndex, startIndex + JOBS_PER_PAGE);
        },
        jobsWithCoordinates() {
            return this.paginatedJobs.filter(job => 
                job.job_location_lat && job.job_location_lng && 
                !isNaN(parseFloat(job.job_location_lat)) && !isNaN(parseFloat(job.job_location_lng))
            );
        },
        noLocationJobs() {
            return this.paginatedJobs.filter(job => 
                !job.job_location_lat || !job.job_location_lng || 
                isNaN(parseFloat(job.job_location_lat)) || isNaN(parseFloat(job.job_location_lng))
            );
        },
        filteredJobsCount() {
            return this.jobsWithCoordinates.length;
        },
        popupStyle() {
            return {
                top: this.popupPosition.top,
                left: this.popupPosition.left,
                maxWidth: '320px',
                width: '320px'
            };
        },
        displayedJobsCount() {
            return this.filteredJobs.length;
        },
        paginationStart() {
            if (this.displayedJobsCount === 0) {
                return 0;
            }

            return (this.currentPage - 1) * JOBS_PER_PAGE + 1;
        },
        paginationEnd() {
            if (this.displayedJobsCount === 0) {
                return 0;
            }

            return Math.min(this.currentPage * JOBS_PER_PAGE, this.displayedJobsCount);
        },
        paginationItems() {
            const total = this.totalPages;
            const current = this.currentPage;

            if (total <= 7) {
                return Array.from({ length: total }, (_, index) => ({
                    key: `page-${index + 1}`,
                    type: 'page',
                    number: index + 1,
                    label: index + 1
                }));
            }

            const items = [
                { key: 'page-1', type: 'page', number: 1, label: 1 }
            ];
            const windowStart = Math.max(2, current - 1);
            const windowEnd = Math.min(total - 1, current + 1);

            if (windowStart > 2) {
                items.push({ key: 'ellipsis-start', type: 'ellipsis', label: '...' });
            }

            for (let page = windowStart; page <= windowEnd; page += 1) {
                items.push({
                    key: `page-${page}`,
                    type: 'page',
                    number: page,
                    label: page
                });
            }

            if (windowEnd < total - 1) {
                items.push({ key: 'ellipsis-end', type: 'ellipsis', label: '...' });
            }

            items.push({ key: `page-${total}`, type: 'page', number: total, label: total });

            return items;
        }
    },
    methods: {
        async fetchJobs() {
            this.toggleLoading(true);
            try {
                const response = await contractorService.getJobs();
                this.jobs = response.data;

                // Initialize map after jobs are loaded if we're in map view
                if (this.viewMode === 'map') {
                    this.$nextTick(() => {
                        this.initializeMap();
                    });
                }
            } catch (error) {
                this.$root.notify('Something went wrong while fetching available jobs, please try again', 'error');
            } finally {
                this.loading = false;
                this.toggleLoading(false);
            }
        },
        getApplications() {
            contractorService.getApplications()
            .then(response => {
                this.applications = response.data;
            });
        },
        formattedPostedDate(creationDateString) {
            if (!creationDateString) {
                return 'Posted recently';
            }

            const normalizedDateString = typeof creationDateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(creationDateString)
                ? `${creationDateString}T00:00:00`
                : creationDateString;
            const creationDate = new Date(normalizedDateString);

            if (Number.isNaN(creationDate.getTime())) {
                return 'Posted recently';
            }

            const now = new Date();
            const diffInMs = now - creationDate;

            if (diffInMs <= 0) {
                return 'Posted recently';
            }

            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60)); // Convert ms to hours

            if (diffInHours < 24) {
                return `Posted ${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
            }

            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays === 1) {
                return "Posted 1 day ago";
            }

            return `Posted ${diffInDays} days ago`;
        },
        formatClosingDate(dateString) {
            if (!dateString) {
                return 'Deadline not specified';
            }

            const parsedDate = new Date(dateString);
            if (Number.isNaN(parsedDate.getTime())) {
                return 'Deadline not specified';
            }

            return parsedDate.toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        getSortedUniqueValues(values) {
            return Array.from(new Set(
                values
                    .map(value => (value || '').trim())
                    .filter(Boolean)
            )).sort((a, b) => a.localeCompare(b));
        },
        buildFilterOptions(values) {
            const options = new Map();

            values.forEach(value => {
                const label = this.normalizeFilterLabel(value);

                if (!label) {
                    return;
                }

                const key = this.normalizeFilterValue(label);
                // Jobs explicitly tagged "Other" fall under the catch-all Other option added below.
                if (!key || key === 'other') {
                    return;
                }

                if (!options.has(key)) {
                    options.set(key, label);
                }
            });

            const sortedOptions = Array.from(options.entries())
                .map(([value, label]) => ({ value, label }))
                .sort((a, b) => a.label.localeCompare(b.label));

            sortedOptions.push({ value: OTHER_FILTER_VALUE, label: 'Other' });

            return sortedOptions;
        },
        normalizeFilterLabel(value) {
            if (Array.isArray(value)) {
                return value.map(item => this.normalizeFilterLabel(item)).filter(Boolean).join(', ');
            }

            return String(value || '').trim();
        },
        normalizeFilterValue(value) {
            return this.normalizeFilterLabel(value).toLowerCase();
        },
        matchesOptionFilter(selectedValue, jobValue, options) {
            if (!selectedValue) {
                return true;
            }

            const normalizedValue = this.normalizeFilterValue(jobValue);
            const knownOptionValues = options
                .filter(option => option.value !== OTHER_FILTER_VALUE)
                .map(option => option.value);

            if (selectedValue === OTHER_FILTER_VALUE) {
                return !normalizedValue || !knownOptionValues.includes(normalizedValue);
            }

            return normalizedValue === selectedValue;
        },
        getJobCategory(job) {
            return this.firstAvailableJobValue(job, [
                'job_category',
                'category',
                'jobCategory',
                'category_name',
                'job_category_name'
            ]);
        },
        getJobSubcategory(job) {
            return this.firstAvailableJobValue(job, [
                'job_subcategory',
                'subcategory',
                'jobSubcategory',
                'subcategory_name',
                'job_subcategory_name'
            ]);
        },
        firstAvailableJobValue(job, keys) {
            for (const key of keys) {
                const value = job?.[key];
                if (Array.isArray(value) && value.length > 0) {
                    return value;
                }
                if (value !== undefined && value !== null && String(value).trim() !== '') {
                    return value;
                }
            }

            return '';
        },
        toggleState(state) {
            if (this.selectedStates.includes(state)) {
                this.selectedStates = this.selectedStates.filter(value => value !== state);
                return;
            }

            this.selectedStates = [...this.selectedStates, state];
        },
        matchesStateFilter(job) {
            if (this.selectedStates.length === 0) {
                return true;
            }

            return this.selectedStates.includes(this.getJobState(job));
        },
        getJobState(job) {
            const location = (job.job_location || '').trim();
            if (!location) {
                return 'Overseas';
            }

            const normalized = location.toLowerCase();
            const stateMatchers = [
                { value: 'ACT', full: 'australian capital territory', abbr: /\bact\b/i },
                { value: 'NSW', full: 'new south wales', abbr: /(^|[\s,()&/-])nsw(?=$|[\s,()&/-])/i },
                { value: 'QLD', full: 'queensland', abbr: /(^|[\s,()&/-])qld(?=$|[\s,()&/-])/i },
                { value: 'WA', full: 'western australia', abbr: /(^|[\s,()&/-])wa(?=$|[\s,()&/-])/i },
                { value: 'SA', full: 'south australia', abbr: /(^|[\s,()&/-])sa(?=$|[\s,()&/-])/i },
                { value: 'VIC', full: 'victoria', abbr: /(^|[\s,()&/-])vic(?=$|[\s,()&/-])/i },
                { value: 'TAS', full: 'tasmania', abbr: /(^|[\s,()&/-])tas(?=$|[\s,()&/-])/i },
                { value: 'NT', full: 'northern territory', abbr: /(^|[\s,()&/-])nt(?=$|[\s,()&/-])/i }
            ];

            for (const matcher of stateMatchers) {
                if (normalized.includes(matcher.full) || matcher.abbr.test(location)) {
                    return matcher.value;
                }
            }

            if (normalized.includes('australia')) {
                return '';
            }

            return 'Overseas';
        },
        normalizeIndustry(job) {
            const value = (job.job_industry || '').toLowerCase();

            if (value.includes('energy')) {
                return 'energy';
            }

            if (value.includes('mining')) {
                return 'mining';
            }

            return '';
        },
        normalizeWorkType(job) {
            const value = (job.job_type || '').toLowerCase().replace(/_/g, ' ').trim();

            if (value.includes('contract')) {
                return 'contract';
            }

            if (value.includes('full time') || value.includes('full-time') || value.includes('permanent')) {
                return 'full-time';
            }

            return '';
        },
        matchesPostedDate(job) {
            if (!this.selectedPostedDate) {
                return true;
            }

            if (!job.job_date) {
                return false;
            }

            const jobDate = new Date(job.job_date);
            if (Number.isNaN(jobDate.getTime())) {
                return false;
            }

            const days = Number(this.selectedPostedDate);
            if (Number.isNaN(days) || days <= 0) {
                return true;
            }

            const threshold = new Date();
            threshold.setDate(threshold.getDate() - days);

            return jobDate >= threshold;
        },
        compareJobs(a, b) {
            if (this.sortBy === 'title_asc') {
                return (a.job_title || '').localeCompare(b.job_title || '');
            }

            if (this.sortBy === 'title_desc') {
                return (b.job_title || '').localeCompare(a.job_title || '');
            }

            const aDate = this.toSortableTimestamp(a.job_date);
            const bDate = this.toSortableTimestamp(b.job_date);

            if (this.sortBy === 'date_asc') {
                if (aDate !== bDate) {
                    return aDate - bDate;
                }
                return (a.job_title || '').localeCompare(b.job_title || '');
            }

            if (aDate !== bDate) {
                return bDate - aDate;
            }

            return (a.job_title || '').localeCompare(b.job_title || '');
        },
        toSortableTimestamp(dateValue) {
            if (!dateValue) {
                return 0;
            }

            const parsed = new Date(dateValue).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        },
        clearFilters() {
            this.searchQuery = '';
            this.sortBy = 'date_desc';
            this.selectedCompany = '';
            this.selectedCategory = '';
            this.selectedSubcategory = '';
            this.selectedPostedDate = '';
            this.selectedStates = [];
            this.selectedIndustry = '';
            this.selectedWorkType = '';
            this.currentPage = 1;
        },
        goToPage(page) {
            const nextPage = Math.min(Math.max(page, 1), this.totalPages);
            this.currentPage = nextPage;
        },
        goToPreviousPage() {
            if (this.currentPage > 1) {
                this.currentPage -= 1;
            }
        },
        goToNextPage() {
            if (this.currentPage < this.totalPages) {
                this.currentPage += 1;
            }
        },
        openJob(job) {
            if (this.isDirectExternal(job) && job.external_job_url) {
                window.open(job.external_job_url, '_blank', 'noopener,noreferrer');
                return;
            }

            if (job.job_slug) {
                this.$router.push('/contractor/job/' + job.job_slug);
            }
        },
        isDirectExternal(job) {
            return Boolean(job && job.is_external && !job.is_imported);
        },
        companyRoute(job) {
            return {
                name: 'ContractorCompanyPage',
                params: { slug: job.company_slug },
                query: { from: 'jobs' }
            };
        },
        hasLocation(job) {
            return job.job_location_lat && job.job_location_lng && 
                !isNaN(parseFloat(job.job_location_lat)) && !isNaN(parseFloat(job.job_location_lng));
        },
        handleSidebarJobClick(job) {
            if (this.hasLocation(job)) {
                this.centerMapOnJob(job);
                return;
            }

            if (job.is_external) {
                this.openJob(job);
                return;
            }

            this.$root.notify('This job does not have location coordinates.', 'info');
        },
        loadGoogleMapsScript() {
            const apiKey = config.googleMaps.apiKey;

            if (!apiKey) {
                console.error('Google Maps API key not configured');
                this.isMapLoading = false;
                return;
            }

            this.isMapLoading = true;
            
            // Check if script is already loaded
            if (window.google && window.google.maps) {
                this.initMap();
                return;
            }
            
            // If script is already being loaded, wait for it
            if (this.mapScriptLoaded && !window.google) {
                // Wait for script to finish loading
                const checkGoogleInterval = setInterval(() => {
                    if (window.google && window.google.maps) {
                        clearInterval(checkGoogleInterval);
                        this.initMap();
                    }
                }, 100);
                return;
            }
            
            // Load the script if not already loaded
            if (!this.mapScriptLoaded) {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
                script.async = true;
                script.defer = true;
                script.onload = () => {
                    this.mapScriptLoaded = true;
                    this.$nextTick(() => {
                        this.initMap();
                    });
                };
                document.head.appendChild(script);
            }
        },
        initMap() {
            if (!this.$refs.mapContainer) {
                this.isMapLoading = false;
                return;
            }
            
            try {
                // Default to Australia if no jobs with coordinates
                const defaultPosition = { lat: -25.2744, lng: 133.7751 };
                
                // Create map with Airbnb-inspired styling
                this.map = new google.maps.Map(this.$refs.mapContainer, {
                    zoom: 4,
                    center: defaultPosition,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.RIGHT_TOP
                    },
                    styles: [
                        {
                            "featureType": "administrative",
                            "elementType": "labels.text.fill",
                            "stylers": [{"color": "#484848"}]
                        },
                        {
                            "featureType": "landscape",
                            "elementType": "all",
                            "stylers": [{"color": "#f7f7f7"}]
                        },
                        {
                            "featureType": "poi",
                            "elementType": "all",
                            "stylers": [{"visibility": "off"}]
                        },
                        {
                            "featureType": "road",
                            "elementType": "all",
                            "stylers": [{"saturation": -100}, {"lightness": 45}]
                        },
                        {
                            "featureType": "transit",
                            "elementType": "all",
                            "stylers": [{"visibility": "simplified"}]
                        },
                        {
                            "featureType": "water",
                            "elementType": "all",
                            "stylers": [{"color": "#0ea5e9"}, {"visibility": "on"}, {"lightness": 70}]
                        }
                    ]
                });
                
                this.bounds = new google.maps.LatLngBounds();
                
                // Add markers for each job with coordinates
                this.addJobMarkers();
                
                this.isMapLoading = false;
            } catch (error) {
                console.error('Error initializing map:', error);
                this.$root.notify('Error loading map. Please try again.', 'error');
                this.isMapLoading = false;
            }
        },
        addJobMarkers() {
            // Clear existing markers
            this.markers.forEach(marker => {
                google.maps.event.clearInstanceListeners(marker);
                marker.setMap(null);
            });
            this.markers = [];
            
            // Filter jobs that have coordinates
            if (this.jobsWithCoordinates.length === 0) return;
            
            // Add markers
            this.jobsWithCoordinates.forEach(job => {
                const position = {
                    lat: parseFloat(job.job_location_lat),
                    lng: parseFloat(job.job_location_lng)
                };
                
                const marker = new google.maps.Marker({
                    position,
                    map: this.map,
                    title: job.job_title,
                    animation: null, // Start with no animation
                    icon: this.getMarkerIcon(false), // Start with unselected icon
                    job: job // Store job reference for easy access
                });
                
                // Add click listener
                marker.addListener('click', () => {
                    this.selectJob(job, marker);
                });
                
                this.markers.push(marker);
                this.bounds.extend(position);
            });
            
            // Fit bounds to show all markers
            if (this.markers.length > 0) {
                this.map.fitBounds(this.bounds);
                
                // Don't zoom in too far on single markers
                const listener = google.maps.event.addListener(this.map, 'idle', () => {
                    if (this.map.getZoom() > 14) {
                        this.map.setZoom(14);
                    }
                    google.maps.event.removeListener(listener);
                });
            }
        },
        getMarkerIcon(isSelected = false) {
            const baseIcon = {
                path: "M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z",
                fillOpacity: 1,
                scale: isSelected ? 2 : 1.5,
                strokeColor: '#ffffff',
                strokeWeight: isSelected ? 2.5 : 1.5,
                anchor: new google.maps.Point(12, 24),
                zIndex: isSelected ? 1000 : 100
            };
            
            if (isSelected) {
                baseIcon.fillColor = '#ef4444'; // Red for selected
                baseIcon.strokeColor = '#ffffff';
            } else {
                baseIcon.fillColor = '#0ea5e9'; // Blue for unselected
                baseIcon.strokeColor = '#ffffff';
            }
            
            return baseIcon;
        },
        selectJob(job, marker) {
            // Clear previous selection
            this.clearSelection();
            
            // Set new selection
            this.selectedJob = job;
            this.selectedMarker = marker;
            
            // Update marker appearance
            marker.setIcon(this.getMarkerIcon(true));
            marker.setZIndex(1000);
            
            // Pan to marker
            this.map.panTo(marker.getPosition());
            
            // Calculate popup position to ensure it's visible
            this.calculatePopupPosition(marker);
        },
        clearSelection() {
            if (this.selectedMarker) {
                // Reset previous marker to unselected state
                this.selectedMarker.setIcon(this.getMarkerIcon(false));
                this.selectedMarker.setZIndex(100);
            }
            
            this.selectedJob = null;
            this.selectedMarker = null;
        },
        calculatePopupPosition(marker) {
            if (!this.map || !marker) return;
            
            // Get the marker's pixel position
            const projection = this.map.getProjection();
            const bounds = this.map.getBounds();
            
            if (projection && bounds) {
                const position = marker.getPosition();
                const pixel = projection.fromLatLngToPoint(position);
                const mapBounds = this.$refs.mapContainer.getBoundingClientRect();
                
                // Calculate relative position within the map container
                const leftPercent = ((pixel.x - projection.fromLatLngToPoint(bounds.getSouthWest()).x) / 
                                   (projection.fromLatLngToPoint(bounds.getNorthEast()).x - projection.fromLatLngToPoint(bounds.getSouthWest()).x)) * 100;
                
                const topPercent = ((projection.fromLatLngToPoint(bounds.getNorthEast()).y - pixel.y) / 
                                  (projection.fromLatLngToPoint(bounds.getNorthEast()).y - projection.fromLatLngToPoint(bounds.getSouthWest()).y)) * 100;
                
                // Ensure popup stays within visible bounds
                let left = `${Math.max(20, Math.min(leftPercent, 60))}%`;
                let top = `${Math.max(10, Math.min(topPercent - 5, 70))}%`;
                
                this.popupPosition = { left, top };
            } else {
                // Fallback positioning
                this.popupPosition = { left: '20px', top: '20px' };
            }
        },
        centerMapOnJob(job) {
            if (!this.hasLocation(job)) {
                this.$root.notify('This job does not have location coordinates.', 'info');
                return;
            }
            
            // Find the marker for this job
            const marker = this.markers.find(m => m.job.job_id === job.job_id);
            
            if (marker) {
                this.selectJob(job, marker);
                this.map.setZoom(14);
            }
        },
        initializeMap() {
            if (this.viewMode === 'map') {
                // Reset map-related variables
                this.map = null;
                this.markers = [];
                this.selectedJob = null;
                this.selectedMarker = null;
                this.isMapLoading = true;
                
                // Load map
                this.loadGoogleMapsScript();
            }
        },
        cleanupMap() {
            // Clear selection
            this.clearSelection();
            
            // Remove all markers from the map
            if (this.markers && this.markers.length) {
                this.markers.forEach(marker => {
                    google.maps.event.clearInstanceListeners(marker);
                    marker.setMap(null);
                });
                this.markers = [];
            }
            
            // Clear map event listeners
            if (this.map) {
                google.maps.event.clearInstanceListeners(this.map);
                this.map = null;
            }
        }
    },
    watch: {
        filteredJobs() {
            if (this.currentPage > this.totalPages) {
                this.currentPage = this.totalPages;
            }

            if (this.viewMode === 'map' && this.map) {
                this.$nextTick(() => {
                    this.addJobMarkers();
                    this.clearSelection();
                });
            }
        },
        searchQuery() {
            this.currentPage = 1;
        },
        sortBy() {
            this.currentPage = 1;
        },
        selectedCompany() {
            this.currentPage = 1;
        },
        selectedCategory() {
            this.currentPage = 1;
            this.selectedSubcategory = '';
        },
        selectedSubcategory() {
            this.currentPage = 1;
        },
        selectedPostedDate() {
            this.currentPage = 1;
        },
        selectedStates: {
            handler() {
                this.currentPage = 1;
            },
            deep: true
        },
        selectedIndustry() {
            this.currentPage = 1;
        },
        selectedWorkType() {
            this.currentPage = 1;
        },
        viewMode(newMode, oldMode) {
            if (newMode === 'map') {
                // Initialize map only if it doesn't exist or was previously destroyed
                if (!this.map) {
                    this.$nextTick(() => {
                        this.initializeMap();
                    });
                }
            } else if (oldMode === 'map' && this.map) {
                // Clean up Google Map resources when switching away from map view
                this.cleanupMap();
            }
        }
    },
    mounted() {
        this.fetchJobs();
    },
    beforeDestroy() {
        this.cleanupMap();
    }
}
</script>

<style scoped>
.container {
    max-width: 1200px;
}

/* Custom scrollbar for the job sidebar */
.overflow-auto::-webkit-scrollbar {
    width: 6px;
}

.overflow-auto::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.overflow-auto::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 10px;
}

.overflow-auto::-webkit-scrollbar-thumb:hover {
    background: #0ea5e9;
}

/* Airbnb-style transitions */
.router-link-active, .router-link {
    transition: all 0.2s ease-in-out;
}

/* Shadow enhancement for cards */
.shadow-lg {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.shadow-xl {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}
</style>
